import React from "react";
import { RadialBarChart, RadialBar, ResponsiveContainer, PolarAngleAxis } from "recharts";
import { User } from "lucide-react";
import type { DiscoveryInfluencerProfile } from "../../types/discoveryProfile.types";

export interface EngagementTabProps {
  influencer: DiscoveryInfluencerProfile;
}

const formatNumber = (num: number): string => {
  if (num >= 1_000_000) return (num / 1_000_000).toFixed(1) + "M";
  if (num >= 1_000) return (num / 1_000).toFixed(1) + "K";
  return num.toString();
};

const formatValue = (num: number, unit: string): string => {
  if (num >= 1_000_000) return (num / 1_000_000).toFixed(1) + "M";
  if (num >= 1_000) return (num / 1_000).toFixed(1) + "K";
  return num.toFixed(unit === "%" ? 2 : 0);
};

export function EngagementTab({ influencer }: EngagementTabProps) {
  // Calculate video view rate (views / followers * 100)
  const videoViewRate = influencer.totalFollowers > 0
    ? ((influencer.avgViews / influencer.totalFollowers) * 100).toFixed(2)
    : "0.00";

  const engagementData = [
    {
      name: "engagement",
      value: Math.min(influencer.engagementRate, 100),
      fill: "#633A89",
    },
  ];

  const benchmarks = [
    {
      icon: "/images/Dashboard/engagement.png",
      label: "Engagement Rate",
      value: influencer.engagementRate,
      benchmark: 3.5,
      unit: "%",
    },
    {
      icon: "/images/Dashboard/likes.png",
      label: "Average Likes",
      value: influencer.avgLikes,
      benchmark: influencer.totalFollowers * 0.03,
      unit: "",
    },
    {
      icon: "/images/Dashboard/comments.png",
      label: "Avg. Comments per post",
      value: influencer.avgComments,
      benchmark: influencer.totalFollowers * 0.002,
      unit: "",
    },
  ];

  return (
    <div className="space-y-6 px-6 max-w-[1200px]">
      {/* Overall Engagement */}
      <section>
        <h2 className="text-2xl font-bold mb-4">Overall Engagement</h2>

        <div className="bg-gradient-to-b from-[#FFFFFF] to-[#F6ECFF] rounded-xl border border-[#CFCFCF] p-6 flex justify-between items-center max-w-[910px]">
          <div>
            <p className="text-2xl font-semibold text-black underline">
              Overall Engagement Rate
            </p>
            <p className="text-[40px] font-bold font-['Inter'] text-purple-700 mt-5">
              {influencer.engagementRate.toFixed(2)}%
            </p>
            <p className="text-lg italic text-black mt-5">
              Across all content type
            </p>
          </div>

          {/* Radial Chart */}
          <div className="flex items-center gap-4">
            <div className="w-[140px] h-[140px]">
              <ResponsiveContainer width={140} height={140}>
                <RadialBarChart
                  innerRadius="70%"
                  outerRadius="100%"
                  data={engagementData}
                  startAngle={90}
                  endAngle={-270}
                >
                  <defs>
                    <linearGradient id="engagementGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                      <stop offset="0%" stopColor="#633A89" />
                      <stop offset="100%" stopColor="#9C81B4" />
                    </linearGradient>
                  </defs>
                  <PolarAngleAxis type="number" domain={[0, 100]} tick={false} />
                  <RadialBar
                    dataKey="value"
                    background={{ fill: "#CFCFCF" }}
                    fill="url(#engagementGradient)"
                  />
                </RadialBarChart>
              </ResponsiveContainer>
            </div>

            <span className="text-[30px] font-['DeliciousHandrawn'] text-purple-700 -mt-4">
              {influencer.engagementRate.toFixed(2)}%
            </span>
          </div>
        </div>
      </section>

      {/* Video Performance */}
      <section>
        <h2 className="text-2xl font-bold mb-4">Video Performance</h2>

        <div className="grid grid-cols-2 gap-4">
          <div className="bg-[#F8F8F8] border rounded-xl p-8 flex justify-between items-center max-w-[500px] h-[170px] relative overflow-hidden">
            <div>
              <p className="text-[#878397] text-2xl font-medium underline">
                Average Video Views
              </p>
              <p className="text-[34px] font-['Inter'] font-bold mt-6">
                {formatNumber(influencer.avgViews)}
              </p>
            </div>
            <img src="/images/Dashboard/views.png" alt="Eye" className="w-[64px] h-[50px] mr-6" />
            <img src="/images/Dashboard/views.png" alt="" className="absolute -right-8 -bottom-8 w-32 h-32 object-contain opacity-[0.1] pointer-events-none" />
          </div>

          <div className="bg-[#F8F8F8] border rounded-xl p-8 flex justify-between items-center max-w-[500px] h-[170px] relative overflow-hidden">
            <div className="flex flex-col justify-between h-full flex-1">
              <div>
                <p className="text-[#878397] text-2xl font-medium underline">
                  Video View Rate
                </p>
                <p className="text-[34px] font-['Inter'] font-bold mt-4">
                  {videoViewRate}%
                </p>
              </div>
              <p className="text-base text-black font-[500] italic text-center w-full">
                % of followers who view videos
              </p>
            </div>
            <User className="text-purple-600" size={28} />
            <img src="/images/Dashboard/views.png" alt="" className="absolute -right-8 -bottom-8 w-32 h-32 object-contain opacity-[0.1] pointer-events-none" />
          </div>
        </div>
      </section>

      {/* Industry Benchmark */}
      <section>
        <h2 className="text-2xl font-bold mb-4">Industry Benchmark</h2>

        <div className="bg-white border rounded-xl p-6 space-y-6">
          {benchmarks.map((b, i) => {
            const percentage = b.benchmark > 0 ? Math.min((b.value / b.benchmark) * 100, 100) : 0;
            return (
              <BenchmarkRow
                key={i}
                icon={b.icon}
                label={b.label}
                value={`${formatValue(b.value, b.unit)}${b.unit}`}
                benchmark={`${formatValue(b.benchmark, b.unit)}${b.unit}`}
                percentage={percentage}
              />
            );
          })}

          <p className="text-[14px] text-[#868296] italic">
            *Benchmarks are estimated based on typical industry standards for {influencer.tier} tier influencers
          </p>
        </div>
      </section>
    </div>
  );
}

function BenchmarkRow({
  icon,
  label,
  value,
  benchmark,
  percentage,
}: {
  icon: string;
  label: string;
  value: string;
  benchmark: string;
  percentage: number;
}) {
  return (
    <div className="flex items-center gap-6">
      {/* Label */}
      <div className="w-[300px] text-xl font-medium text-black flex items-center gap-3">
        <div className="w-10 h-10 bg-[#F4F4F4] rounded-lg flex items-center justify-center shrink-0">
          <img src={icon} alt={`${label} icon`} className="w-5 h-5 object-contain" />
        </div>
        {label}
      </div>

      {/* Progress Bar Track */}
      <div className="flex-1 h-[6px] bg-gray-300 rounded-full relative flex items-center">
        <div
          className="absolute left-0 top-0 h-full bg-[#9333EA] rounded-full"
          style={{ width: `${percentage}%` }}
        >
          <div className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-1/2 w-[14px] h-[14px] bg-[#9333EA] rounded-full border-[1px] border-white" />
        </div>
      </div>

      {/* Values */}
      <div className="flex items-center gap-2 min-w-[120px] justify-end">
        <div className="text-xl text-[#9333EA] font-semibold font-['Inter']">
          {value}
        </div>
        <div className="text-xl text-gray-400 font-semibold font-['Inter']">
          | {benchmark}
        </div>
      </div>
    </div>
  );
}

export default EngagementTab;
